import { useState } from "react";
import { NavLink, Route, Routes } from "react-router-dom";
import OverviewPage from "./pages/OverviewPage";
import EasPage from "./pages/EasPage";
import EaDetailPage from "./pages/EaDetailPage";
import SystemPage from "./pages/SystemPage";
import { logout } from "./components/LoginGate";

const NAV: Array<{ to: string; label: string; end?: boolean; icon: string }> = [
  { to: "/", label: "Overview", end: true, icon: "▦" },
  { to: "/eas", label: "EAs", icon: "◆" },
  { to: "/system", label: "System", icon: "⚙" },
];

// Management-panel shell: fixed sidebar (lg+) with vertical nav, full-width
// content. Mobile keeps the slide-down sheet from the previous layout so
// the sidebar doesn't eat the screen on small viewports.
export default function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <div className="min-h-screen bg-canvas-dark text-body lg:flex">
      {/* Sidebar — fixed on lg+, slides down on mobile. */}
      <aside
        className={`shrink-0 border-b border-hairline-dark bg-canvas-dark/95 lg:sticky lg:top-0 lg:h-screen lg:w-60 lg:border-b-0 lg:border-r`}
      >
        <div className="flex items-center justify-between px-5 py-4 lg:flex-col lg:items-stretch lg:gap-6 lg:py-6">
          <NavLink to="/" className="flex items-baseline gap-2" onClick={() => setMobileOpen(false)}>
            <span className="text-lg font-bold tracking-wider text-primary">ELVIS</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted">admin</span>
          </NavLink>
          <div className="flex items-center gap-3 lg:hidden">
            <span className="h-2 w-2 rounded-full bg-trading-up" title="elvis-nest online" />
            <button
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              className="rounded-md border border-hairline-dark p-2 text-on-dark"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
          <nav className="hidden flex-col gap-1 px-2 text-sm lg:flex">
            {NAV.map((n) => (
              <SideNavItem key={n.to} {...n} />
            ))}
          </nav>
          <div className="hidden border-t border-hairline-dark px-5 pt-4 text-[11px] text-muted lg:mt-auto lg:block">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-trading-up" />
              <span>elvis-nest online</span>
            </div>
            <div className="mt-1">VNT · 15s auto-refresh</div>
            {/* Without this there is no way to drop a stored credential or
                switch accounts short of clearing localStorage by hand. */}
            <button
              type="button"
              onClick={logout}
              className="mt-3 w-full rounded-md border border-hairline-dark px-3 py-1.5 text-left text-[11px] text-muted hover:border-trading-down/50 hover:text-trading-down"
            >
              Sign out
            </button>
          </div>
        </div>
        {mobileOpen ? (
          <nav className="border-t border-hairline-dark px-4 pb-3 lg:hidden">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-base font-medium ${
                    isActive ? "bg-surface-elevated text-primary" : "text-on-dark hover:bg-surface-elevated/50"
                  }`
                }
              >
                <span className="text-muted">{n.icon}</span>
                <span>{n.label}</span>
              </NavLink>
            ))}
          </nav>
        ) : null}
      </aside>

      <main className="flex-1 px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/eas" element={<EasPage />} />
          <Route path="/eas/:eaId" element={<EaDetailPage />} />
          <Route path="/system" element={<SystemPage />} />
        </Routes>
      </main>
    </div>
  );
}

function SideNavItem({
  to,
  end,
  icon,
  label,
}: {
  to: string;
  end?: boolean;
  icon: string;
  label: string;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `group flex items-center gap-3 rounded-md px-3 py-2 font-medium transition ${
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted hover:bg-surface-elevated/50 hover:text-on-dark"
        }`
      }
    >
      <span className="text-base text-muted-strong group-[.text-primary]:text-primary">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

function MenuIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
